import { useEffect, useReducer, useState } from 'react';
import { useStore } from '../../../../store/state';
import {
  RequestDeleteInfluencerTaxInformation,
  RequestGetInfluencerTaxInformation,
  RequestPutInfluencerTaxInformation,
} from '../../../../store/User/actions';
import { Address, TaxInformationAPI } from '../../../../store/User/types';
import dayjs from 'dayjs';
import { taxInfoReducer } from './reducer';
import { useResponsive } from '../../../../hooks';
import { Breakpoint } from '../../../styling/breakpoints';
import { defaultTaxInfo } from '../../Register/data';
import { validateVAT } from '../../../../utils/vatValidation.utils';

export const transformTaxInfo = (
  taxInfo: typeof defaultTaxInfo,
  currentState: TaxInformationAPI | null,
  address: Address
): TaxInformationAPI => {
  return {
    influencer_vat_registered: taxInfo.isVATRegistered === 'yes',
    influencer_vat_number: taxInfo.vatNumber,
    influencer_limited_company: taxInfo.isLLC === 'yes',
    influencer_company_name: taxInfo.companyName,
    influencer_company_number: taxInfo.companyNumber,
    should_include_vat: taxInfo.receiveVAT === 'yes',

    effective_from: dayjs().toISOString(),
    works_via_agency: currentState?.works_via_agency ?? false,
    invoice_subject: currentState?.invoice_subject ?? 'influencer',
    influencer_tax_country:
      currentState?.influencer_tax_country ?? address.country,
    influencer_home_address: currentState?.influencer_home_address ?? {
      type: 'influencer_home',
      line_one: address.line_one,
      line_two: address.line_two,
      city: address.city,
      postcode: address.postcode,
    },
  };
};

export const useTaxInformation = () => {
  const [{ userState }, dispatch] = useStore();
  const storeTaxInformation = userState.taxInformation;

  const { layout } = useResponsive();
  const isDesktop = layout > Breakpoint.MOBILE;

  const [isEditing, setIsEditing] = useState(false);

  const [taxInformation, taxDispatch] = useReducer(
    taxInfoReducer,
    defaultTaxInfo
  );

  const influencerId = userState.user?.influencer_id;

  useEffect(() => {
    if (influencerId)
      RequestGetInfluencerTaxInformation(dispatch, influencerId);
  }, [dispatch, influencerId]);

  useEffect(() => {
    if (storeTaxInformation) {
      taxDispatch({
        type: 'INITIALISE',
        payload: storeTaxInformation,
      });
    }
  }, [storeTaxInformation]);

  const toggleEdit = () => setIsEditing(!isEditing);

  const onDelete = () => {
    if (influencerId && storeTaxInformation?.id)
      RequestDeleteInfluencerTaxInformation(
        dispatch,
        influencerId,
        storeTaxInformation?.id
      ).then(() => {
        taxDispatch({ type: 'CLEAR' });
        toggleModal();
      });
  };

  const onSubmit = () => {
    if (influencerId && !!userState.user?.influencer_address)
      RequestPutInfluencerTaxInformation(
        dispatch,
        influencerId,
        transformTaxInfo(
          taxInformation,
          storeTaxInformation,
          userState.user.influencer_address
        )
      ).then(toggleEdit);
  };

  const onClose = () => {
    taxDispatch({ type: 'INITIALISE', payload: storeTaxInformation });
    toggleEdit();
  };

  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = () => setIsOpen(!isOpen);

  const isVATValid =
    (taxInformation.isVATRegistered === 'yes' &&
      validateVAT(
        userState.user?.influencer_address?.country ?? '',
        taxInformation.vatNumber
      ) &&
      taxInformation.vatNumber != '') ||
    taxInformation.isVATRegistered === 'no';

  const [validVAT, setValidVAT] = useState(isVATValid);

  useEffect(() => {
    setValidVAT(
      validateVAT(
        userState.user?.influencer_address?.country ?? '',
        taxInformation.vatNumber
      )
    );
  }, [userState.user?.influencer_address?.country, taxInformation.vatNumber]);

  const isLLCValid =
    (taxInformation.isLLC === 'yes' &&
      taxInformation.companyName !== '' &&
      taxInformation.companyNumber !== '') ||
    taxInformation.isLLC === 'no';

  return {
    taxInformation,
    dispatch,
    isEditing,
    onDelete,
    onSubmit,
    toggleEdit,
    isLoading: userState.isLoading.taxInformation,
    error: userState.errors.taxInformation,
    taxDispatch,
    isDesktop,
    canDeleteTaxInformation: !!storeTaxInformation?.id,
    onClose,
    isOpen,
    toggleModal,
    country: userState.user?.influencer_address?.country ?? '',
    disableSubmit: !isLLCValid || !isVATValid,
    validVAT,
  };
};
